import {
  Autocomplete,
  Box,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useGetAllUsersQuery } from "../../../app/authApiSlice";
import { selectCurrentUser } from "../auth/authSlice";
import { useGetActivityLogsQuery } from "./activityApiSlice";
import { formatUtcDatetime } from "../../../helpers/dateUtils";

const PER_PAGE = 10;

function formatDate(iso) {
  return formatUtcDatetime(iso);
}

function formatEntity(row) {
  if (row?.entity_label) return `${row.entity_type}: ${row.entity_label}`;
  if (row?.entity_id) return `${row.entity_type}: #${row.entity_id}`;
  return row?.entity_type || "—";
}

export default function ActivityLogs() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useSelector(selectCurrentUser);
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userInputValue, setUserInputValue] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activityType, setActivityType] = useState("");
  const debounceRef = useRef(null);

  const { data: usersData, isFetching: usersFetching } = useGetAllUsersQuery(
    {
      school_id: user?.school_id,
      page: 1,
      per_page: 10,
      q: userSearch,
    },
    { skip: !user?.school_id }
  );
  const users = usersData?.users ?? [];

  const handleUserInputChange = useCallback((_, value) => {
    setUserInputValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setUserSearch(value);
    }, 300);
  }, []);

  const { data, isLoading, isFetching } = useGetActivityLogsQuery({
    page,
    per_page: PER_PAGE,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    activity_type: activityType || undefined,
    actor_user_id: selectedUser?.id || undefined,
  });

  const activities = data?.activities ?? [];
  const totalPages = data?.pages ?? 1;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} mb={2}>
        Activity Logs
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" }, gap: 2, mb: 2 }}>
        <TextField
          label="Date From"
          type="date"
          size="small"
          value={dateFrom}
          onChange={(event) => {
            setDateFrom(event.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Date To"
          type="date"
          size="small"
          value={dateTo}
          onChange={(event) => {
            setDateTo(event.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
        />
        <Autocomplete
          options={users}
          getOptionLabel={(option) => option?.username || "—"}
          value={selectedUser}
          onChange={(_, value) => {
            setSelectedUser(value);
            setPage(1);
          }}
          inputValue={userInputValue}
          onInputChange={handleUserInputChange}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options) => options}
          loading={usersFetching}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Actor Username"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {usersFetching && <CircularProgress color="inherit" size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          clearOnEscape
        />
        <TextField
          select
          label="Activity Type"
          size="small"
          value={activityType}
          onChange={(event) => {
            setActivityType(event.target.value);
            setPage(1);
          }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="CREATE">Create</MenuItem>
          <MenuItem value="UPDATE">Update</MenuItem>
          <MenuItem value="DELETE">Delete</MenuItem>
        </TextField>
      </Box>

      {(isLoading || isFetching) && <LinearProgress sx={{ mb: 1 }} />}

      {isMobile ? (
        <Box>
          {activities.length === 0 && !isLoading && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
              No activity found for the selected filters.
            </Paper>
          )}
          {activities.map((row) => (
            <Paper key={row.id} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
              <Typography sx={{ fontWeight: 600 }}>{row.activity_type || "—"}</Typography>
              <Typography variant="caption" color="text.secondary">{formatDate(row.created_at)}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Actor: {row.actor_username || "—"}</Typography>
              <Typography variant="body2" color="text.secondary">Entity: {formatEntity(row)}</Typography>
              <Typography variant="body2" sx={{ mt: 0.75 }}>{row.message || "—"}</Typography>
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                <TableCell>Date</TableCell>
                <TableCell>Actor Username</TableCell>
                <TableCell>Activity Type</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No activity found for the selected filters.
                  </TableCell>
                </TableRow>
              )}
              {activities.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(row.created_at)}</TableCell>
                  <TableCell>{row.actor_username || "—"}</TableCell>
                  <TableCell>{row.activity_type || "—"}</TableCell>
                  <TableCell>{formatEntity(row)}</TableCell>
                  <TableCell>{row.message || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {data?.total != null
            ? `Page ${data.page} of ${data.pages} (${data.total} activit${data.total === 1 ? "y" : "ies"})`
            : ""}
        </Typography>
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
            size={isMobile ? "small" : "medium"}
          />
        )}
      </Box>
    </Box>
  );
}
